import { cloneElement } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Popover, Tooltip } from "antd";
import { DollarOutlined, EnvironmentOutlined, IdcardOutlined, TagOutlined, TeamOutlined, UpCircleOutlined, UserOutlined } from "@ant-design/icons";

import styles from "./index.module.less";
import Authentication from "../../../util/Authentication";
import { RouteConstants } from "../../../constants/ApplicationConstants/RouteConstants";
import LocalStorageUtil from "../../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../../constants/ApplicationConstants/LocalStorageConstants";
import { updateMasterFilters } from "../../../appRedux/reducers/Common";
import { ModuleAndIdMapping, Modules } from "../../../constants/DatabaseConstants/MasterConstant";

import type FixMeLater from "../../../shared/types/fixMeLater.type";
import { MasterFiltersConstant } from "../../../constants/ApplicationConstants/MasterConstant";

const ICON_MAPPING: Record<string, any> = {
  [Modules.CUSTOMER_MASTER]: <UserOutlined />,
  [Modules.CUSTOMER_LOCATION_MASTER]: <EnvironmentOutlined />,
  [Modules.CUSTOMER_CLIENT_MASTER]: <TeamOutlined />,
  [Modules.KEY_FOB_MASTER]: <TagOutlined />,
  [Modules.KEY_FOB_ALLOCATION_MASTER]: <IdcardOutlined />,
  [Modules.PRICING_MASTER]: <DollarOutlined />
};

interface CustomPopoverActionsProps {
  moduleId: number;
  record: FixMeLater;
}

interface filterInterface {
  customerId: number | null;
  locationId?: number | null;
  clientId?: number | null;
  keyFobId?: number | null;
}

const CustomPopoverActions = ({ moduleId, record }: CustomPopoverActionsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const updateTopBarFilters = (filters: filterInterface) => {
    const dataObj = {
      [MasterFiltersConstant.CUSTOMER_ID]: filters?.customerId,
      [MasterFiltersConstant.LOCATION_ID]: filters?.locationId,
      [MasterFiltersConstant.CLIENT_ID]: filters?.clientId,
      [MasterFiltersConstant.KEY_FOB_ID]: filters?.keyFobId,
      refresh: false
    };

    LocalStorageUtil.localStorageUpdateData(LocalStorageConstants.MASTER_FILTERS, dataObj);
    dispatch(updateMasterFilters(dataObj));
  };

  const handleRedirect = (currentModuleId: number, redirectModuleId: number, record: any) => {
    const moduleName = Authentication.getModuleNameById(redirectModuleId);

    const filters = (() => {
      switch (currentModuleId) {
        case ModuleAndIdMapping[Modules.CUSTOMER_MASTER]:
          return { customerId: record?.id, locationId: null };
        case ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]:
          return { customerId: record?.customer_id, locationId: record?.id };
        case ModuleAndIdMapping[Modules.CUSTOMER_CLIENT_MASTER]:
          return { customerId: record?.customer_id, locationId: record?.customer_location_id, clientId: record?.id };
        case ModuleAndIdMapping[Modules.KEY_FOB_MASTER]:
          return { customerId: record?.customer_id, locationId: record?.customer_location_id, keyFobId: record?.id };
        case ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]:
          return { customerId: record?.customer_id, locationId: record?.customer_location_id };
        case ModuleAndIdMapping[Modules.PRICING_MASTER]:
          return { customerId: record?.customer_id, locationId: record?.customer_location_id };
        default:
          return { customerId: record?.customer_id, locationId: record?.customer_location_id };
      }
    })();

    updateTopBarFilters(filters);
    navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/${moduleName}`);
  };

  const formatTitle = (key: string) => {
    return key?.replace(/-/g, " ")?.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getTooltipConfig = () =>
    Object.entries(ModuleAndIdMapping)
      .filter(([key]) => key in ICON_MAPPING)
      .filter(([key, redirectModuleId]) => redirectModuleId !== moduleId)
      .map(([key, redirectModuleId]) => ({
        title: formatTitle(key),
        icon: ICON_MAPPING[key],
        redirectModuleId
      }));

  const TOOLTIP_CONFIG = getTooltipConfig();

  const popoverContent = (
    <div className={styles["main-container"]}>
      <div className={styles["icon-grid"]}>
        {TOOLTIP_CONFIG.map(({ title, icon, redirectModuleId }) => (
          <Tooltip key={title} title={title}>
            {cloneElement(icon, {
              className: `gx-link ${styles["icon"]}`,
              onClick: record?.is_active ? () => handleRedirect(moduleId, redirectModuleId, record) : undefined
            })}
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <Popover content={popoverContent} title={null} placement="topRight" trigger="click">
      <UpCircleOutlined className={`gx-link  ${moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER] ? "" : "gx-ml-3"} ${styles["action-icon"]} ${!record?.is_active ? styles["disabled"] : ""}`} />
    </Popover>
  );
};

export default CustomPopoverActions;
